import {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query'

import Toast from 'components/Basic/Toast'
import {AIUsageUser} from 'interfaces'
import {AIUsageService} from 'services/api'

interface AIUsageUsersQueryResponseType {
  fetchNextPage: () => Promise<UseInfiniteQueryResult>
  hasNextPage: boolean
  users: AIUsageUser[]
  isLoading: boolean
  isFetchingNextPage: boolean
  isSuccess: boolean
  isError: boolean
  error: unknown
}

interface UseAIUsageUsersQueryOptions {
  enabled?: boolean
}

export const AI_USAGE_USERS_QUERY_KEY = 'getAIUsageUsers'

export const useAIUsageUsersQuery = ({
  enabled = true,
}: UseAIUsageUsersQueryOptions = {}): AIUsageUsersQueryResponseType => {
  const {t} = useTranslation(['aiUsage'])

  const {
    data,
    fetchNextPage,
    isLoading,
    isSuccess,
    isError,
    error,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [AI_USAGE_USERS_QUERY_KEY],
    queryFn: ({pageParam}) => {
      return AIUsageService.getAIUsageUsers({
        after: pageParam,
      })
    },
    getNextPageParam: lastPage => {
      const pageInfo = lastPage?.data?.pageInfo
      return pageInfo?.hasNextPage ? pageInfo.endCursor : undefined
    },
    enabled,
  })

  const hasNextPage = useMemo(() => {
    const lastPage = data?.pages?.at(-1)
    return Boolean(lastPage?.data?.pageInfo?.hasNextPage)
  }, [data?.pages])

  useEffect(() => {
    if (isError) {
      Toast({
        message: t('aiUsage:AI_USAGE_TOAST_USERS_FETCH_ERROR'),
        type: 'error',
        customId: 'ai-usage-users-fetch-error',
      })
    }
  }, [isError, t])

  const users = useMemo(() => {
    return data?.pages?.flatMap(page => page?.data?.users) ?? []
  }, [data?.pages])

  return {
    fetchNextPage,
    hasNextPage,
    users,
    isLoading,
    isFetchingNextPage,
    isSuccess,
    isError,
    error,
  }
}
